import { gql } from '@apollo/client';
import { useState } from 'react';
import {
  RuleStatus,
  useUpdateRuleMutation,
} from '../../hinge-connect/rules/types';

const GET_RULE = gql`
  query getRule($id: String!) {
    getRule(id: $id) {
      id
      status
      definition {
        name
      }
      createdAt
      updatedAt
      source
      resourceTypes
    }
  }
`;

type UseStatusToggleResult = {
  loading: boolean;
  error: string | null;
  canToggleStatus: (currentStatus: RuleStatus) => boolean;
  getNextStatus: (currentStatus: RuleStatus) => RuleStatus | null;
  updateRuleStatus: (ruleId: string, newStatus: RuleStatus) => Promise<void>;
};

export const useStatusToggle = (): UseStatusToggleResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateRuleMutation] = useUpdateRuleMutation();

  const canToggleStatus = (currentStatus: RuleStatus): boolean =>
    [RuleStatus.Published, RuleStatus.Unpublished, RuleStatus.Draft].includes(
      currentStatus,
    );

  const getNextStatus = (currentStatus: RuleStatus): RuleStatus | null => {
    switch (currentStatus) {
      case RuleStatus.Published:
        return RuleStatus.Unpublished;
      case RuleStatus.Unpublished:
        return RuleStatus.Published;
      case RuleStatus.Draft:
        return RuleStatus.Published;
      default:
        return null;
    }
  };

  const updateRuleStatus = async (
    ruleId: string,
    newStatus: RuleStatus,
  ): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await updateRuleMutation({
        variables: {
          id: ruleId,
          updateRuleInput: {
            status: newStatus,
          },
        },
        // Update cache to force refresh
        update: cache => {
          // Evict the specific rule from cache to force refetch
          cache.evict({
            id: cache.identify({ __typename: 'Rule', id: ruleId }),
          });
          // Also evict any getRule queries for this rule
          cache.gc();
        },
        // Refetch queries to ensure data consistency
        refetchQueries: [
          'listRules',
          {
            query: GET_RULE,
            variables: { id: ruleId },
          },
        ],
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update rule status';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    canToggleStatus,
    getNextStatus,
    updateRuleStatus,
  };
};
